import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { products } from "../data/products";
import styled from "styled-components";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { QUOTE_EMAIL_CONFIG, isEmailConfigured } from "../config/emailConfig";
import SEO from "../components/SEO";
import StructuredData, {
  ProductSchema,
  BreadcrumbSchema,
} from "../components/StructuredData";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  console.log("ID:", id);
  console.log("All products:", products);

  const product = products.find((p) => p.id === Number(id));

  console.log("Found product:", product);

  // Add state for quote form
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (!product) {
    return (
      <>
        <SEO
          title="Product Not Found"
          description="The requested chandelier product could not be found."
          url={`/product/${id}`}
          noindex={true}
        />
        <DetailContainer>
          <h2>Product not found</h2>
          <BackButton onClick={() => navigate("/products")}>
            Back to Products
          </BackButton>
        </DetailContainer>
      </>
    );
  }

  // Handle product image URL - webpack imports return module strings
  const productImageUrl = typeof product.image === 'string' 
    ? (product.image.startsWith("http") 
        ? product.image 
        : `https://chandelierluxe.co.za${product.image}`)
    : (product.image || "https://chandelierluxe.co.za/assets/images/hero-chandelier.jpeg");
  
  const breadcrumbs = [
    { name: "Home", url: "https://chandelierluxe.co.za/" },
    { name: "Products", url: "https://chandelierluxe.co.za/products" },
    { name: product.name, url: `https://chandelierluxe.co.za/product/${product.id}` },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEmailConfigured(QUOTE_EMAIL_CONFIG)) {
      setSubmitStatus("error");
      return;
    }
    setIsLoading(true);
    setSubmitStatus(null);

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        message: formData.message,
        product_name: product.name,
        product_id: product.id,
        product_dimensions: product.dimensions,
        to_email: QUOTE_EMAIL_CONFIG.recipientEmail,
        inquiry_type: "Quote Request",
      };

      await emailjs.send(
        QUOTE_EMAIL_CONFIG.serviceId,
        QUOTE_EMAIL_CONFIG.templateId,
        templateParams,
        QUOTE_EMAIL_CONFIG.publicKey
      );

      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending quote request:", error);
      setSubmitStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title={product.name}
        description={`${product.name} - ${product.dimensions}. Premium crystal chandelier available from Chandelier Luxe. Request a quote for professional installation in Johannesburg, South Africa.`}
        keywords={`${product.name}, crystal chandelier ${product.dimensions}, luxury lighting, chandelier for sale Johannesburg`}
        url={`/product/${product.id}`}
        image={productImageUrl}
        type="product"
      />
      <StructuredData data={ProductSchema(product)} />
      <StructuredData data={BreadcrumbSchema(breadcrumbs)} />
      <DetailContainer
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
      <BackButton onClick={() => navigate("/products")}>
        ← Back to Products
      </BackButton>

      <ContentWrapper>
        <ImageSection>
          <ImageContainer>
            <ProductImage src={product.image} alt={product.name} />
            {product.isNew && <NewBadge>New</NewBadge>}
          </ImageContainer>
        </ImageSection>

        <InfoSection>
          <ProductName>{product.name}</ProductName>
          <ProductDimensions>
            Dimensions: {product.dimensions}
          </ProductDimensions>

          <QuoteForm onSubmit={handleSubmit}>
            <h2>Request a Quote</h2>

            {submitStatus === "success" && (
              <SuccessMessage>
                Thank you for your quote request! We'll get back to you soon
                with pricing information.
              </SuccessMessage>
            )}

            {submitStatus === "error" && (
              <ErrorMessage>
                {!isEmailConfigured(QUOTE_EMAIL_CONFIG)
                  ? "Email is not configured. Please add REACT_APP_EMAILJS_PUBLIC_KEY to your .env file (see .env.example)."
                  : "Sorry, there was an error sending your quote request. Please try again or contact us directly."}
              </ErrorMessage>
            )}

            <FormGroup>
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                placeholder={`I'm interested in the ${product.name}. Please provide more information.`}
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={isLoading}>
              {isLoading ? "Sending..." : "Request Quote"}
            </SubmitButton>
          </QuoteForm>
        </InfoSection>
      </ContentWrapper>
    </DetailContainer>
    </>
  );
};

// Styled Components
const DetailContainer = styled.div`
  max-width: 1800px;
  margin: 8rem auto 4rem;
  padding: 0 2rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.accent};
  font-size: 1.1rem;
  cursor: pointer;
  margin-bottom: 2rem;
  padding: 0.5rem 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ImageSection = styled.div`
  position: relative;
  background: rgba(44, 44, 44, 0.8);
  border-radius: 16px;
  padding: 2rem;
  height: fit-content;
`;

const ImageContainer = styled.div`
  position: relative;
  background: rgba(44, 44, 44, 0.8);
  border-radius: 16px;
  padding: 2rem;
`;

const ProductImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
`;

const InfoSection = styled.div`
  padding: 2rem;
  background: rgba(44, 44, 44, 0.8);
  border-radius: 16px;
`;

const ProductName = styled.h1`
  font-size: 2.5rem;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: 1rem;
`;

const ProductDimensions = styled.p`
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;

const NewBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.primary};
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
`;

const QuoteForm = styled.form`
  margin-top: 2rem;
  padding: 2rem;
  background: rgba(26, 26, 26, 0.8);
  border: 1px solid rgba(192, 160, 128, 0.2);
  border-radius: 16px;

  h2 {
    color: ${(props) => props.theme.colors.text};
    margin-bottom: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    color: ${(props) => props.theme.colors.text};
    margin-bottom: 0.5rem;
  }

  input,
  textarea {
    width: 100%;
    padding: 0.8rem;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(192, 160, 128, 0.2);
    border-radius: 4px;
    color: ${(props) => props.theme.colors.text};

    &:focus {
      outline: none;
      border-color: ${(props) => props.theme.colors.accent};
    }
  }

  textarea {
    height: 120px;
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.primary};
  padding: 1rem 2rem;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(192, 160, 128, 0.2);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #22c55e;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
`;

export default ProductDetail;
